// from https://craft.mxkaske.dev/post/fancy-multi-select

import React from 'react'
import { X } from 'lucide-react'
import { cn } from '@/helpers/utils'

import { Badge } from '@/components/ui/badge'
import {
  Command,
  CommandGroup,
  CommandItem,
  CommandList
} from '@/components/ui/command'
import { Command as CommandPrimitive } from 'cmdk'

const MultiSelect = ({
  options = [],
  selected: initialSelected = [],
  onChange,
  placeholder = 'Select items...',
  className = '',
  error = false
}) => {
  const inputRef = React.useRef(null)
  const containerRef = React.useRef(null)
  const [open, setOpen] = React.useState(false)
  const [selected, setSelected] = React.useState(initialSelected)
  const [inputValue, setInputValue] = React.useState('')
  const [position, setPosition] = React.useState('bottom')
  const [isFocused, setIsFocused] = React.useState(false)

  // Remove the global keydown handler and fix the component instead
  React.useEffect(() => {
    // Disable defaultChecked on all buttons in our component to prevent auto-focus
    const disableDefaultOnButtons = () => {
      const buttons = containerRef.current?.querySelectorAll('button')
      if (buttons) {
        buttons.forEach(button => {
          button.defaultChecked = false
          button.setAttribute('tabindex', '-1') // Make it not focusable by keyboard
        })
      }
    }

    disableDefaultOnButtons()
  }, [selected]) // Re-run when selected items change

  const selectables = options.filter(
    (option) => !selected.some((s) => s.value === option.value)
  )

  const filteredSelectables = selectables.filter((item) =>
    item.label.toLowerCase().includes(inputValue.toLowerCase())
  )

  // Check if we need to position the dropdown above or below
  const updatePosition = React.useCallback(() => {
    if (!containerRef.current) return

    const container = containerRef.current
    const rect = container.getBoundingClientRect()
    const spaceBelow = window.innerHeight - rect.bottom
    const spaceNeeded = Math.min(filteredSelectables.length * 36, 240) // Approx height needed for dropdown

    setPosition(spaceBelow < spaceNeeded && rect.top > spaceNeeded ? 'top' : 'bottom')
  }, [filteredSelectables.length])

  React.useEffect(() => {
    if (open) {
      updatePosition()
      window.addEventListener('scroll', updatePosition)
      window.addEventListener('resize', updatePosition)
    }

    return () => {
      window.removeEventListener('scroll', updatePosition)
      window.removeEventListener('resize', updatePosition)
    }
  }, [open, updatePosition])

  const handleUnselect = React.useCallback((item, e) => {
    if (e) {
      e.preventDefault()
      e.stopPropagation()
    }
    const newSelected = selected.filter((s) => s.value !== item.value)
    setSelected(newSelected)
    onChange?.(newSelected)
  }, [selected, onChange])

  const handleKeyDown = React.useCallback((e) => {
    // Only handle keydown when the component is focused
    if (!isFocused) return

    const input = inputRef.current
    if (input) {
      if (e.key === 'Delete' || e.key === 'Backspace') {
        if (input.value === '') {
          const newSelected = [...selected]
          newSelected.pop()
          setSelected(newSelected)
          onChange?.(newSelected)
        }
      }
      if (e.key === 'Escape') {
        input.blur()
        setOpen(false)
      }
    }
  }, [selected, onChange, isFocused])

  const handleFocus = React.useCallback(() => {
    setOpen(true)
    setIsFocused(true)
    inputRef.current?.focus()
  }, [])

  const handleInputBlur = React.useCallback((e) => {
    // Only close if we're not clicking inside the container
    if (!containerRef.current?.contains(e.relatedTarget)) {
      setOpen(false)
      setIsFocused(false)
    }
  }, [])

  // Handle container blur
  const handleContainerBlur = React.useCallback((e) => {
    if (!containerRef.current?.contains(e.relatedTarget)) {
      setIsFocused(false)
    }
  }, [])

  return (
    <div
      ref={containerRef}
      className='relative w-full'
      onBlur={handleContainerBlur}
      onFocus={() => setIsFocused(true)}
    >
      <Command
        onKeyDown={handleKeyDown}
        shouldFilter={false}
        className={`overflow-visible bg-transparent ${className}`}
      >
        <div
          onClick={handleFocus}
          className='group rounded-md border border-input p-2 text-sm ring-offset-background focus-within:border-ring transition-[color,box-shadow] shadow-xs'
        >
          <div className='flex flex-wrap gap-1'>
            {selected.map((item) => (
              <Badge key={item.value} variant='secondary' className='h-8'>
                {item.label}
                <button
                  type='button'
                  tabIndex='-1' // Explicitly make not focusable by tab/keyboard
                  className='ml-1 rounded-full outline-none ring-offset-background focus:ring-1 focus:ring-ring focus:ring-offset-1'
                  onKeyDown={(e) => {
                    e.stopPropagation()
                    if (e.key === 'Enter') {
                      e.preventDefault()
                      handleUnselect(item, e)
                    }
                  }}
                  onMouseDown={(e) => {
                    e.preventDefault()
                    e.stopPropagation()
                  }}
                  onClick={(e) => {
                    e.preventDefault()
                    e.stopPropagation()
                    handleUnselect(item, e)
                  }}
                >
                  <X className='h-3 w-3 text-muted-foreground hover:text-foreground' />
                </button>
              </Badge>
            ))}
            <CommandPrimitive.Input
              ref={inputRef}
              value={inputValue}
              onValueChange={setInputValue}
              onFocus={() => {
                setOpen(true)
                setIsFocused(true)
              }}
              onBlur={handleInputBlur}
              placeholder={placeholder}
              className='flex-1 bg-transparent outline-none border-0 placeholder:text-muted-foreground ring-0 text-sm p-[5px]'
            />
          </div>
        </div>
        {open && filteredSelectables.length > 0 && (
          <div
            className={cn(
              'absolute z-50 w-full rounded-md border bg-popover text-popover-foreground shadow-md outline-none',
              position === 'top'
                ? 'bottom-full mb-1 slide-in-from-top-2'
                : 'top-full mt-1 slide-in-from-bottom-2'
            )}
          >
            <CommandList className='max-h-60 overflow-auto'>
              <CommandGroup className='h-full'>
                {filteredSelectables.map((item) => (
                  <CommandItem
                    key={item.value}
                    onMouseDown={(e) => {
                      e.preventDefault()
                      e.stopPropagation()
                    }}
                    onSelect={() => {
                      setInputValue('')
                      const newSelected = [...selected, item]
                      setSelected(newSelected)
                      onChange?.(newSelected)
                      // Keep focus in the input after selection
                      setTimeout(() => {
                        inputRef.current?.focus()
                      }, 0)
                    }}
                    className='cursor-pointer'
                  >
                    {item.label}
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </div>
        )}
      </Command>
    </div>
  )
}

export default MultiSelect
